/**
 * @fileoverview Client-side experiment dimensions for analytics fan-out.
 * @description Reads fetched assignments (client bootstrap store; DOM script
 * kept as a legacy/test fallback) and persists surface-filtered dimensions so
 * every `sendEvent`/`track` call carries experiment context.
 */

import type { EventPropValue } from "~/lib/analytics/registry"

import {
  areAssignmentsFetched,
  getFetchedAssignments,
  getFetchedLedgerIngestToken,
  refreshAssignments,
} from "./assignments-client-store"
import {
  experimentDimensions,
  filterAssignmentsForPath,
  type ExperimentAssignment,
} from "./dimensions"
import { getExperimentDefinition } from "./registry"
import { LEDGER_INGEST_TOKEN_ELEMENT_ID } from "./ledger-ingest-client"
import { isSurfaceMatch } from "./surfaces"
import type { ExperimentMetric } from "./types"

/**
 * Refresh the ingest token this many seconds BEFORE its embedded expiry.
 * Covers request latency + clock skew; the TTL itself is 24h.
 */
const LEDGER_TOKEN_REFRESH_MARGIN_SECONDS = 300

/**
 * Parses the unix expiry out of a `visitorId.exp.sig` ledger ingest token.
 *
 * @param {string} token - Token as minted by `createLedgerIngestToken`.
 * @returns {number | undefined} Expiry in unix seconds, if parseable.
 */
export function ledgerIngestTokenExpirySeconds(
  token: string,
): number | undefined {
  const parts = token.split(".")
  if (parts.length !== 3) {
    return undefined
  }
  const exp = Number(parts[1])
  return Number.isFinite(exp) ? exp : undefined
}

/**
 * Reads the ledger ingest token, refreshing it first when missing or within
 * the expiry margin. An SPA tab open past the 24h TTL otherwise kept POSTing
 * the stale token forever (the store treated the first fetch as final): the
 * ingest guard 403'd and the Postgres ledger silently missed conversions and
 * consent-delayed exposures that GA4 still recorded — exactly the rows that
 * matter once the ledger is authoritative (#233 `r3517282446`). The refresh
 * is deduped by the store and best-effort: on failure the stale token is
 * returned so the POST still carries the strongest credential available.
 *
 * @returns {Promise<string | undefined>} A fresh (or best-available) token.
 */
export async function readFreshLedgerIngestToken(): Promise<
  string | undefined
> {
  const current = readLedgerIngestToken()
  if (current) {
    const exp = ledgerIngestTokenExpirySeconds(current)
    const nowSeconds = Math.floor(Date.now() / 1000)
    if (
      exp !== undefined &&
      exp - nowSeconds > LEDGER_TOKEN_REFRESH_MARGIN_SECONDS
    ) {
      return current
    }
  }
  try {
    await refreshAssignments()
  } catch {
    // Best-effort — fall through to whatever is available.
  }
  return getFetchedLedgerIngestToken() ?? current
}

/** SessionStorage key for active experiment assignments. */
export const ACTIVE_EXPERIMENTS_STORAGE_KEY = "adsights_active_experiments_v1"

/** DOM id for the server-injected assignment bootstrap payload. */
export const ASSIGNMENTS_BOOTSTRAP_ELEMENT_ID =
  "adsights-experiment-assignments"

/**
 * Reads the server-minted ledger ingest token from the bootstrap payload.
 *
 * @returns {string | undefined} HMAC token when present.
 */
export function readLedgerIngestToken(): string | undefined {
  const fetched = getFetchedLedgerIngestToken()
  if (fetched) {
    return fetched
  }
  if (typeof document === "undefined") {
    return undefined
  }
  const el = document.getElementById(LEDGER_INGEST_TOKEN_ELEMENT_ID)
  const raw = el?.textContent?.trim()
  if (!raw) {
    return undefined
  }
  try {
    const parsed = JSON.parse(raw) as unknown
    return typeof parsed === "string" && parsed.length > 0 ? parsed : undefined
  } catch {
    return raw.length > 0 ? raw : undefined
  }
}

/**
 * Reads assignments from the server bootstrap script tag.
 *
 * @returns {readonly ExperimentAssignment[]} Parsed assignments or empty array.
 */
export function readBootstrappedAssignments(): readonly ExperimentAssignment[] {
  if (areAssignmentsFetched()) {
    return getFetchedAssignments()
  }
  if (typeof document === "undefined") {
    return []
  }
  const el = document.getElementById(ASSIGNMENTS_BOOTSTRAP_ELEMENT_ID)
  if (!el?.textContent) {
    return []
  }
  try {
    const parsed = JSON.parse(el.textContent) as ExperimentAssignment[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * True when assignments are AUTHORITATIVELY known for this page — either the
 * client bootstrap fetch resolved, or a (legacy/test) server bootstrap element
 * exists in the DOM. Distinguishes "empty because nothing has loaded yet /
 * we're on a non-bootstrap page" (keep the sessionStorage fallback for
 * cross-page conversion attribution) from "empty because the server says
 * nothing is running" (stale storage must be cleared).
 *
 * @returns {boolean} Whether an authoritative assignment source is present.
 */
export function hasAssignmentsBootstrap(): boolean {
  if (areAssignmentsFetched()) {
    return true
  }
  if (typeof document === "undefined") {
    return false
  }
  return document.getElementById(ASSIGNMENTS_BOOTSTRAP_ELEMENT_ID) !== null
}

/**
 * Returns `sessionStorage` when accessible, else `null`. Even `typeof
 * sessionStorage` THROWS under some privacy policies (Chrome with cookies
 * blocked raises a SecurityError from the accessor itself), so the probe
 * must sit inside the try — otherwise `sendEvent`'s dimension enrichment
 * would throw before the GA/Vercel emit and storage-blocked visitors would
 * lose every experiment-enriched event (#233 `r3516615480`).
 *
 * @returns {Storage | null} Accessible sessionStorage or null.
 */
function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage
  } catch {
    return null
  }
}

/**
 * Removes the persisted assignment dimensions (always permitted — deleting
 * first-party analytics state needs no consent).
 *
 * @returns {void}
 */
export function clearActiveExperiments(): void {
  const storage = safeSessionStorage()
  if (!storage) {
    return
  }
  try {
    storage.removeItem(ACTIVE_EXPERIMENTS_STORAGE_KEY)
  } catch {
    // Storage may be blocked.
  }
}

/**
 * Persists surface-eligible assignments for the current page.
 *
 * An authoritative-but-empty eligible set CLEARS the stored key instead of
 * leaving it in place: otherwise a paused/concluded experiment's stale
 * assignment kept flowing into GA4 dimensions via the sessionStorage fallback
 * until the tab session ended (#233 `r3515451596`).
 *
 * @param {readonly ExperimentAssignment[]} assignments - All request assignments.
 * @param {string} pathname - Current pathname.
 * @returns {void}
 */
export function persistActiveExperiments(
  assignments: readonly ExperimentAssignment[],
  pathname: string,
): void {
  const storage = safeSessionStorage()
  if (!storage) {
    return
  }
  const eligible = filterAssignmentsForPath(assignments, pathname)
  if (eligible.length === 0) {
    // NO-OP, not a clear: with the beacon mounted from the ROOT layout it
    // runs on every route, and an off-surface page (e.g. /request-access
    // after a `/` exposure) simply has nothing NEW to persist — wiping the
    // snapshot here would destroy the exposure-time dimensions that
    // cross-page funnel events depend on (#233 `r3519603538`). Clearing is
    // reserved for the AUTHORITATIVE empty (fetch resolved, nothing
    // running) and the opt-out path, both handled by the beacon itself.
    return
  }
  try {
    // MERGE with the existing snapshot (exposure-time union): overwriting
    // would drop experiments exposed on earlier pages this session.
    // Same-experiment entries refresh to the newest assignment.
    const persisted = readPersistedAssignments()
    const merged = [
      ...persisted.filter(
        (entry) =>
          !eligible.some((a) => a.experiment_id === entry.experiment_id),
      ),
      ...eligible,
    ]
    storage.setItem(ACTIVE_EXPERIMENTS_STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // Storage may be blocked.
  }
}

/**
 * Reads the persisted active-experiment set (exposure-time snapshot).
 *
 * Entries were surface-filtered AT EXPOSURE TIME by
 * `persistActiveExperiments`, so they are already surface-qualified — callers
 * must NOT re-filter them against the current pathname: the whole point of
 * persisting is carrying the exposed visitor's dimensions onto post-exposure
 * funnel pages (`/` exposure → `/request-access` submit) where the
 * experiment's surface no longer matches (#233 `r3516324365`).
 *
 * @returns {readonly ExperimentAssignment[]} Persisted assignments or empty array.
 */
function readPersistedAssignments(): readonly ExperimentAssignment[] {
  const storage = safeSessionStorage()
  if (!storage) {
    return []
  }
  try {
    const raw = storage.getItem(ACTIVE_EXPERIMENTS_STORAGE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw) as ExperimentAssignment[]
    if (!Array.isArray(parsed)) {
      return []
    }
    // Registry-status guard: the authoritative-empty clearing only runs where
    // the bootstrap mounts (homepage), so a visitor who lands directly on a
    // funnel page after an experiment was paused/concluded would otherwise
    // keep emitting that experiment's dims and conversion POSTs from the
    // persisted set until the tab session ends. The bundled registry carries
    // the deploy-time status — drop anything that is no longer running
    // (#233 `r3516809925`).
    return parsed.filter(
      (assignment) =>
        getExperimentDefinition(assignment.experiment_id)?.status ===
        "running",
    )
  } catch {
    return []
  }
}

/**
 * Merges current-page-eligible assignments with the exposure-time persisted
 * set, deduped by experiment id (current-page entries win the slot order, so
 * the top-level `experiment_id`/`variant_id` dimensions stay the on-page
 * experiment when both exist; persisted-only experiments ride along on the
 * indexed `exp_N_*` keys).
 *
 * A union — not an either/or fallback — because a `global`-surface experiment
 * makes the eligible set non-empty on EVERY page, which would otherwise
 * permanently mask the persisted path-scoped exposure it coexists with.
 *
 * @param {readonly ExperimentAssignment[]} eligible - Surface-eligible on this page.
 * @param {readonly ExperimentAssignment[]} persisted - Exposure-time persisted set.
 * @returns {readonly ExperimentAssignment[]} Deduped union.
 */
function mergeWithPersisted(
  eligible: readonly ExperimentAssignment[],
  persisted: readonly ExperimentAssignment[],
): readonly ExperimentAssignment[] {
  if (persisted.length === 0) {
    return eligible
  }
  const seen = new Set(eligible.map((a) => a.experiment_id))
  return [...eligible, ...persisted.filter((a) => !seen.has(a.experiment_id))]
}

/**
 * Reads persisted experiment dimensions for analytics enrichment.
 *
 * Combines what's eligible on the current page (from the bootstrap fetch)
 * with the exposure-time persisted set — the latter un-refiltered, honoring
 * the "dimensions on every goal event after exposure" contract in
 * `docs/analytics/experiments.md` for post-exposure funnel pages
 * (`/` exposure → `/request-access` submit) (#233 `r3516324365`).
 *
 * @returns {Record<string, EventPropValue>} Flat properties to merge into events.
 */
export function readActiveExperimentDimensions(
  eventAction?: string,
): Record<string, EventPropValue> {
  if (typeof window === "undefined") {
    return {}
  }

  const pathname = window.location.pathname
  const eligible = filterAssignmentsForPath(
    readBootstrappedAssignments(),
    pathname,
  )
  const candidates = mergeWithPersisted(eligible, readPersistedAssignments())

  // Guardrail metrics measure ON-SURFACE behavior, and the GA4 fallback
  // rollup counts them from these event DIMENSIONS (not from
  // `resolveConversionTargets`, which already scopes the ledger side). So a
  // persisted `/`-scoped assignment must not stamp its dimensions onto an
  // off-surface guardrail event — a visitor exposed on `/` who scrolls
  // /request-access would otherwise count as a homepage guardrail conversion
  // in every GA4-fallback analysis run, skewing hold/promote
  // (#233 `r3517282439`). Non-guardrail events keep the cross-page union —
  // primary funnel metrics deliberately follow the visitor.
  const scoped =
    eventAction === undefined
      ? candidates
      : candidates.filter((assignment) => {
          const definition = getExperimentDefinition(assignment.experiment_id)
          const isGuardrail =
            definition?.guardrailMetrics?.includes(
              eventAction as ExperimentMetric,
            ) ?? false
          if (!isGuardrail) {
            return true
          }
          return isSurfaceMatch(assignment.surface, pathname)
        })

  return experimentDimensions(scoped)
}

/**
 * Resolves ledger conversion targets for a goal event on the current page.
 *
 * Mirrors `readActiveExperimentDimensions`: current-page-eligible assignments
 * are unioned with the surface-qualified persisted set (NOT re-filtered by
 * pathname) so a path-scoped experiment whose goal metric fires
 * post-navigation still records its ledger conversion (#233 `r3516324365`).
 * Guardrail metrics are the exception — they only count on-surface (see
 * below).
 *
 * @param {string} metric - GA4 event name.
 * @param {string} pathname - Current pathname.
 * @returns {readonly ExperimentAssignment[]} Eligible experiments whose primary metric matches.
 */
export function resolveConversionTargets(
  metric: string,
  pathname: string,
): readonly ExperimentAssignment[] {
  if (typeof window === "undefined") {
    return []
  }

  const eligible = filterAssignmentsForPath(
    readBootstrappedAssignments(),
    pathname,
  )
  const candidates = mergeWithPersisted(eligible, readPersistedAssignments())

  return candidates.filter((assignment) => {
    const metricName = metric as ExperimentMetric
    if (assignment.primary_metric === metricName) {
      return true
    }
    const definition = getExperimentDefinition(assignment.experiment_id)
    if (!(definition?.guardrailMetrics?.includes(metricName) ?? false)) {
      return false
    }
    // Guardrail metrics (scroll_depth, …) measure ON-SURFACE page behavior,
    // so unlike primary funnel goals they do NOT follow the visitor across
    // navigation: a persisted `/`-scoped assignment must not turn every
    // post-navigation scroll into a homepage guardrail conversion — that
    // would skew the hold/promote decision (#233 `r3516537439`).
    return isSurfaceMatch(assignment.surface, pathname)
  })
}
